/* ══════════════════════════════════════════════════════════════════
   Was in einem Belegtext gerade getippt steht — für alle, die ihn zeigen.

   Paket 9, Luca 07.10.2026: *„Wenn ich auf Texte klicke, übernimmt nicht
   der Text den Text, der jetzt zum Beispiel im Nachtext schon steht."*

   Derselbe Satz steht an zwei Stellen: im Feld unter den Positionen
   (`Belegtext`) und im Blatt „Texte" (`Texteformular`, am Telefon
   `TexteBlattTelefon`). Das Feld schreibt erst beim Verlassen, und das
   Schreiben läuft, während das Blatt schon aufgeht — das Blatt nahm sich
   dann den Stand von vorher und hielt ihn fest. Wer dort sicherte, schrieb
   den alten Satz über den neuen.

   Darum liegt der ungesicherte Text hier, je Vorgang, Beleg und Stelle,
   und jede Stelle liest ihn, bevor sie den gesicherten Stand nimmt. Ohne
   React und ohne Abhängigkeiten, damit ein Test es ohne Browser prüft.
   ══════════════════════════════════════════════════════════════════ */

import type { Belegart, Belegtextstand } from "../daten/typen";

export type Textstelle = "vor" | "nach";

/** Die Belege nach dem Angebot — sie stehen im Blatt „Texte". */
export type Folgebeleg = Exclude<Belegart, "angebot">;

/** Vortext und Nachtext je Folgebeleg, so wie das Blatt sie hält. */
export type Entwurf = Record<Folgebeleg, { vor: string; nach: string }>;

const entwuerfe = new Map<string, string>();
const hoerer = new Set<() => void>();

const schluessel = (nr: string, beleg: string, stelle: Textstelle) => `${nr}|${beleg}|${stelle}`;

/** Der ungesicherte Text — `undefined`, wenn niemand etwas getippt hat. */
export function entwurfLesen(nr: string, beleg: string, stelle: Textstelle): string | undefined {
  return entwuerfe.get(schluessel(nr, beleg, stelle));
}

/** Getippt, noch nicht gesichert. */
export function entwurfSetzen(nr: string, beleg: string, stelle: Textstelle, text: string): void {
  if (!nr) return;
  entwuerfe.set(schluessel(nr, beleg, stelle), text);
  hoerer.forEach((h) => h());
}

/** Gesichert oder verworfen — dann gilt wieder der Stand. */
export function entwurfLoeschen(nr: string, beleg: string, stelle: Textstelle): void {
  if (entwuerfe.delete(schluessel(nr, beleg, stelle))) hoerer.forEach((h) => h());
}

/** Für `useSyncExternalStore` — wer zeigt, hört mit. */
export function entwurfAbonnieren(h: () => void): () => void {
  hoerer.add(h);
  return () => { hoerer.delete(h); };
}

/**
 * **Was im Feld steht: der Entwurf vor dem Stand.** Gesichert ist der Stand;
 * was jemand gerade getippt hat, ist neuer.
 */
export function textImFeld(nr: string, beleg: string, stelle: Textstelle, stand: string): string {
  return entwurfLesen(nr, beleg, stelle) ?? stand;
}

/**
 * **Ein Baustein kommt dazu, er ersetzt nicht still.**
 *
 * Bis zum 07.10.2026 setzte die Wahl eines Bausteins den Text des Feldes
 * auf den Baustein — ein Nachtext mit drei Absätzen war mit einem Klick
 * weg und gleich gesichert. Jetzt gilt:
 *
 * - Leeres Feld: der Baustein.
 * - Steht er schon wörtlich darin: nichts doppelt.
 * - Sonst: angehängt, mit einer Leerzeile dazwischen.
 */
export function bausteinEinfuegen(alt: string, baustein: string): string {
  const neu = baustein.trim();
  if (!neu) return alt;
  if (!alt.trim()) return baustein;
  if (alt.includes(neu)) return alt;
  return `${alt.trimEnd()}\n\n${neu}`;
}

/**
 * Der Stand je Belegart — mit `nr` zusätzlich, was gerade in einem Feld des
 * Vorgangs getippt und noch nicht gesichert ist (`belegtextentwurf.ts`).
 * Ohne `nr` der gesicherte Stand allein: die Grundlage für „geändert?".
 */
export function entwurfAus(stand: Belegtextstand[], nr?: string): Entwurf {
  const von = (art: Folgebeleg) => {
    const z = stand.find((s) => s.art === art);
    const vor = z?.vortext.eigen ?? "";
    const nach = z?.nachtext.eigen ?? "";
    return nr
      ? { vor: textImFeld(nr, art, "vor", vor), nach: textImFeld(nr, art, "nach", nach) }
      : { vor, nach };
  };
  return { auftragsbestaetigung: von("auftragsbestaetigung"), lieferschein: von("lieferschein"), rechnung: von("rechnung") };
}

/**
 * Was je Belegart hinausgeht — **nur die Felder, die sich geändert haben.**
 * Bis zum 07.10.2026 gingen immer beide, und ein Vortext, im Blatt geändert,
 * schrieb den Nachtext von vorhin mit: über den, der eben im Feld darunter
 * gesichert worden war (Paket 9).
 */
export function geaenderteFelder(
  entwurf: { vor: string; nach: string },
  anfang: { vor: string; nach: string },
): { vortext?: string | null; nachtext?: string | null } {
  const felder: { vortext?: string | null; nachtext?: string | null } = {};
  if (entwurf.vor.trim() !== anfang.vor.trim()) felder.vortext = entwurf.vor.trim() || null;
  if (entwurf.nach.trim() !== anfang.nach.trim()) felder.nachtext = entwurf.nach.trim() || null;
  return felder;
}
